import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import actionParearImoview from 'src/actions/usuarios/actionParearImoview';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Alert, AlertDescription } from 'src/@/components/ui/alert';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import Iconify from 'src/components/Iconify';

const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

// Quantos saves em voo ao mesmo tempo. Cada um faz um login BLOQUEANTE na
// Imoview no backend (timeout de 15s), então disparar o lote inteiro de uma vez
// significava dezenas de logins simultâneos no mesmo convênio: o parceiro
// throttlava ou o proxy estourava o tempo, e o painel reportava falha para
// corretores cuja senha estava correta — que então precisavam ser redigitados.
const LIMITE_SAVES_SIMULTANEOS = 3;

/**
 * Executa as tarefas com no máximo `limite` em voo, devolvendo os resultados na
 * MESMA ordem e no mesmo formato de Promise.allSettled — o chamador continua
 * casando resultado[i] com a tarefa[i].
 */
async function executarComLimite(tarefas, limite) {
    const resultados = new Array(tarefas.length);
    let proximo = 0;
    const worker = async () => {
        while (proximo < tarefas.length) {
            const i = proximo;
            proximo += 1;
            try {
                resultados[i] = { status: 'fulfilled', value: await tarefas[i]() };
            } catch (reason) {
                resultados[i] = { status: 'rejected', reason };
            }
        }
    };
    await Promise.all(
        Array.from({ length: Math.min(limite, tarefas.length) }, worker),
    );
    return resultados;
}

// Passos 2 e 3 do fluxo de "Envio de Atendimento": depois que o Passo 1
// (credenciais + Carregar) traz um `resumo` de sincronização, mostra na
// própria página — nada de modal — quem casou por email, deixa parear
// manualmente quem não casou (Passo 2), e por fim oferece o campo de senha
// pra TODOS os sincronizados (Passo 3).
//
// O Passo 3 é OPCIONAL: o atendimento é criado pela conta fixa do Passo 1 e
// transferido pro corretor (App_AlterarCorretor), que só precisa do
// codigoUsuario resolvido no Passo 2. A senha pessoal continua aqui porque a
// credencial segue existindo no cadastro do usuário e é o único lugar de onde
// dá pra atualizá-la em lote.
export default function ConciliacaoImoviewSection({ resumo, usuarios, onPareado }) {
    const { t } = useTranslation('settings');

    // Passo 2: { [laisId]: imoviewUserObj } — seleção de correspondência.
    const [pareamentos, setPareamentos] = useState({});
    // Passo 3: { [laisId]: senha em texto puro, só em memória até o submit
    // — nunca persistida como veio, o backend converte pra hash MD5.
    const [senhas, setSenhas] = useState({});
    const [revelarSenhas, setRevelarSenhas] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [resultadoSalvar, setResultadoSalvar] = useState(null); // { ok, msg }

    // Hooks precisam rodar sempre na mesma ordem — usa defaults seguros em
    // vez de "return null" antes deles; o early return real fica no fim.
    const casados = resumo?.detalheCasados || [];
    const laisSemImoview = resumo?.laisSemImoview || [];
    const imoviewSemLais = resumo?.imoviewSemLais || [];

    const codigosUsados = useMemo(
        () => new Set(Object.values(pareamentos).map((u) => u.codigoUsuario)),
        [pareamentos],
    );

    const escolher = (laisId, codigoUsuarioStr) => {
        const imo = imoviewSemLais.find((u) => String(u.codigoUsuario) === codigoUsuarioStr);
        setPareamentos((prev) => {
            const next = { ...prev };
            if (imo) next[laisId] = imo;
            else delete next[laisId];
            return next;
        });
        setSenhas((prev) => {
            if (!(laisId in prev)) return prev;
            const next = { ...prev };
            delete next[laisId];
            return next;
        });
    };

    const alterarSenha = (laisId, value) => {
        setSenhas((prev) => ({ ...prev, [laisId]: value }));
    };

    // Status geral: todo mundo com identidade Imoview confirmada
    // (codigoUsuario), venha de quando vier (sync de hoje, de semana
    // passada, pareamento manual antigo...) — direto do banco, sempre
    // visível assim que a tela abre, sem precisar clicar em "Carregar".
    const sincronizados = useMemo(() => (usuarios || [])
        .filter((u) => u?.config?.imoview?.codigoUsuario != null)
        .map((u) => ({
            laisId: String(u._id),
            nomeLais: u.nomeFantasia || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || String(u._id),
            nome: u.config.imoview.nome,
            cargo: u.config.imoview.cargo,
            // É este código que vai como `codigoNovoCorretor` na transferência
            // do atendimento — precisa ser conferível na tela.
            codigoUsuario: u.config.imoview.codigoUsuario,
            comSenha: !!u.config.imoview.senhaMd5,
            senhaAtualizadaEm: u.config.imoview.senhaAtualizadaEm || null,
        })), [usuarios]);

    // Passo 3: fonte principal é o BANCO (usuarios, buscado fresco a cada
    // carga da página) — não o `resumo` da última sincronização (esse é só
    // estado local, some ao recarregar). Lista TODOS que têm identidade
    // confirmada (codigoUsuario), não só quem falta senha — quem já tem
    // (comSenha=true) também precisa aparecer aqui pra dar pra corrigir uma
    // senha que ficou desatualizada no Imoview (o campo fica opcional pra
    // esses: só sobrescreve se a pessoa digitar algo).
    const pendentesDoBanco = useMemo(() => (usuarios || [])
        .filter((u) => u?.config?.imoview?.codigoUsuario != null)
        .map((u) => ({
            laisId: String(u._id),
            nomeLais: u.nomeFantasia || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || String(u._id),
            nome: u.config.imoview.nome,
            cargo: u.config.imoview.cargo,
            email: u.config.imoview.identidadeEmail,
            codigoUsuario: u.config.imoview.codigoUsuario,
            creci: u.config.imoview.creci,
            perfil: u.config.imoview.perfil,
            setor: u.config.imoview.setor,
            situacao: u.config.imoview.situacao,
            comSenha: !!u.config.imoview.senhaMd5,
        })), [usuarios]);

    // Seleções feitas AGORA no Passo 2 (ainda não salvas, então não estão em
    // `usuarios` ainda) entram/sobrescrevem por cima da lista do banco.
    const pendentesSenha = useMemo(() => {
        const porId = new Map(pendentesDoBanco.map((p) => [p.laisId, p]));
        Object.entries(pareamentos).forEach(([laisId, imo]) => {
            porId.set(laisId, {
                laisId,
                nomeLais: laisSemImoview.find((lu) => lu.laisId === laisId)?.nome || porId.get(laisId)?.nomeLais || laisId,
                nome: imo.nome,
                cargo: imo.cargo,
                email: imo.email,
                codigoUsuario: imo.codigoUsuario,
                creci: imo.creci,
                perfil: imo.perfil,
                setor: imo.setor,
                situacao: imo.situacao,
                comSenha: false,
            });
        });
        return [...porId.values()];
    }, [pendentesDoBanco, pareamentos, laisSemImoview]);

    // Salva quem tem senha digitada OU um pareamento novo do Passo 2 ainda não
    // persistido. O pareamento manual precisa entrar mesmo sem senha: a senha
    // virou opcional (o atendimento é criado pela conta fixa e transferido pelo
    // codigoUsuario), e antes um match sem senha simplesmente não era gravado.
    // Senha filtrada JÁ com trim — é o mesmo valor que será enviado, então um
    // campo só de espaços não conta como preenchido.
    const prontosParaSalvar = pendentesSenha.filter(
        (p) => (senhas[p.laisId] || '').trim() || pareamentos[p.laisId],
    );

    const handleSalvar = async () => {
        if (!prontosParaSalvar.length) return;
        setSalvando(true);
        setResultadoSalvar(null);
        // Uma falha não pode derrubar os saves dos demais nem esconder QUEM
        // falhou e POR QUÊ — o backend valida cada login na Imoview e responde
        // 422 quando a senha é recusada. Concorrência limitada para não abrir
        // dezenas de logins simultâneos no parceiro (ver LIMITE_SAVES_SIMULTANEOS).
        // Senha vai com trim (espaço colado vira hash MD5 errado depois).
        const resultados = await executarComLimite(
            prontosParaSalvar.map((p) => () => actionParearImoview(p.laisId, {
                codigoUsuario: p.codigoUsuario,
                nome: p.nome,
                creci: p.creci,
                cargo: p.cargo,
                perfil: p.perfil,
                setor: p.setor,
                situacao: p.situacao,
                email: (p.email || '').trim(),
                senha: (senhas[p.laisId] || '').trim(),
            })),
            LIMITE_SAVES_SIMULTANEOS,
        );

        const sucesso = [];
        const falhas = [];
        // O backend responde 200 + `aviso` quando GRAVOU sem conseguir validar o
        // login (sem chave de convênio, ou Imoview fora do ar). Sem propagar esse
        // aviso, a tela pintaria de verde um lote inteiro de senhas nunca
        // verificadas — o mesmo "save cego" que esta série de commits eliminou.
        const avisos = new Set();
        resultados.forEach((r, i) => {
            const p = prontosParaSalvar[i];
            if (r.status === 'fulfilled') {
                sucesso.push(p);
                const aviso = r.value?.data?.aviso;
                if (aviso) avisos.add(aviso);
            } else {
                falhas.push({ p, msg: r.reason?.response?.data?.mensagem || t('aiAgents.realstate.envioAtendimento.pareamentoErro', 'Erro ao salvar.') });
            }
        });
        const avisoTexto = avisos.size > 0 ? ` ${[...avisos].join(' ')}` : '';

        if (falhas.length === 0) {
            setResultadoSalvar({
                // Com aviso pendente não é sucesso limpo: o painel fica âmbar
                // para o operador não sair achando que os logins foram conferidos.
                ok: avisos.size === 0,
                msg: t('aiAgents.realstate.envioAtendimento.pareamentoSucesso', '{{n}} usuário(s) atualizado(s) com sucesso.', { n: sucesso.length }) + avisoTexto,
            });
        } else {
            const detalhes = falhas.map(({ p, msg }) => `${p.nome || p.email}: ${msg}`).join(' | ');
            setResultadoSalvar({
                ok: false,
                msg: (sucesso.length > 0
                    ? t('aiAgents.realstate.envioAtendimento.pareamentoParcial', '{{ok}} salvo(s); {{erro}} com falha — {{detalhes}}', { ok: sucesso.length, erro: falhas.length, detalhes })
                    : detalhes) + avisoTexto,
            });
        }

        // Só os que salvaram de fato saem do Passo 2/3 — os que falharam
        // continuam listados com a senha digitada, prontos pra corrigir.
        if (sucesso.length > 0) {
            const idsProntos = new Set(sucesso.map((p) => p.laisId));
            setPareamentos((prev) => {
                const next = { ...prev };
                idsProntos.forEach((id) => delete next[id]);
                return next;
            });
            setSenhas((prev) => {
                const next = { ...prev };
                idsProntos.forEach((id) => delete next[id]);
                return next;
            });
            if (onPareado) await onPareado(sucesso);
        }
        setSalvando(false);
    };

    // Status geral mostra sempre que há algo sincronizado no banco. Passo 2
    // só existe com um sync fresco desta sessão (`resumo`). Passo 3 mostra
    // quem está pendente no banco mesmo sem sincronizar agora. Sem nenhum
    // dos três, não tem nada pra mostrar.
    if (!resumo && sincronizados.length === 0) return null;

    return (
        <div className="flex flex-col gap-4">
            {/* Status geral — sempre visível, direto do banco */}
            {sincronizados.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col gap-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-[#7C3AED]">
                            {t('aiAgents.realstate.envioAtendimento.statusLabel', 'Usuários já sincronizados')}
                        </Label>
                        <p className="text-xs text-gray-500 mt-1">
                            {t(
                                'aiAgents.realstate.envioAtendimento.statusHint',
                                'Identidade já confirmada no Imoview — estes usuários podem receber atendimentos. Não precisa clicar em "Carregar" de novo pra ver isso.',
                            )}
                        </p>
                    </div>
                    <div className="rounded-xl border border-gray-100 overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColLais', 'Usuário LAIS')}</TableHead>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColImoview', 'Usuário Imoview')}</TableHead>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColCodigo', 'Código Imoview')}</TableHead>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColCargo', 'Cargo')}</TableHead>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColStatus', 'Status')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sincronizados.map((s) => (
                                    <TableRow key={s.laisId}>
                                        <TableCell className="text-sm">{s.nomeLais}</TableCell>
                                        <TableCell className="text-sm">{s.nome}</TableCell>
                                        <TableCell className="text-sm">
                                            <span className="inline-flex items-center rounded-md bg-violet-50 px-2 py-0.5 font-mono text-xs text-violet-700">
                                                {s.codigoUsuario}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-sm text-gray-500">{s.cargo || '—'}</TableCell>
                                        {/* Identidade conciliada já basta pra receber atendimentos —
                                            a senha é informação secundária, não um bloqueio. */}
                                        <TableCell className="text-sm">
                                            <div className="flex flex-col">
                                                <span className="inline-flex items-center gap-1 text-green-700">
                                                    <Iconify icon="eva:checkmark-circle-2-fill" width={14} />
                                                    {t('aiAgents.realstate.envioAtendimento.statusPronto', 'Pronto')}
                                                </span>
                                                {s.comSenha ? (
                                                    s.senhaAtualizadaEm && (
                                                        <span className="text-[11px] text-gray-400">
                                                            {t('aiAgents.realstate.envioAtendimento.statusSenhaDesde', 'senha desde {{data}}', { data: new Date(s.senhaAtualizadaEm).toLocaleDateString('pt-BR') })}
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="text-[11px] text-gray-400">
                                                        {t('aiAgents.realstate.envioAtendimento.statusSemSenha', 'sem senha pessoal (opcional)')}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}

            {/* Passo 2 */}
            {resumo && (
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-[#7C3AED]">
                            {t('aiAgents.realstate.envioAtendimento.passo2Label', 'Passo 2 — Usuários sincronizados')}
                        </Label>
                        <p className="text-xs text-gray-500 mt-1">
                            {t(
                                'aiAgents.realstate.envioAtendimento.passo2Hint',
                                '{{casados}} casado(s) automaticamente por email • {{semImoview}} sem correspondência — selecione manualmente quem é quem.',
                                { casados: casados.length, semImoview: laisSemImoview.length },
                            )}
                        </p>
                    </div>
                </div>

                {casados.length > 0 && (
                    <div className="rounded-xl border border-gray-100 overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColLais', 'Usuário LAIS')}</TableHead>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColImoview', 'Usuário Imoview')}</TableHead>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColCodigo', 'Código Imoview')}</TableHead>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColCargo', 'Cargo')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {casados.map((c) => (
                                    <TableRow key={c.laisId}>
                                        <TableCell className="text-sm">{c.nomeLais}</TableCell>
                                        <TableCell className="text-sm">{c.nome}</TableCell>
                                        <TableCell className="text-sm">
                                            <span className="inline-flex items-center rounded-md bg-violet-50 px-2 py-0.5 font-mono text-xs text-violet-700">
                                                {c.codigoUsuario}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-sm text-gray-500">{c.cargo || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {/* A gravação dos pareamentos acontece no botão do Passo 3 —
                    sem esse aviso o operador seleciona todo mundo aqui e sai da
                    tela achando que salvou. */}
                {Object.keys(pareamentos).length > 0 && (
                    <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                        <AlertDescription className="text-xs">
                            {t(
                                'aiAgents.realstate.envioAtendimento.passo2SalvarLembrete',
                                'Seleção feita — clique em "Salvar" no Passo 3 abaixo para gravar o pareamento. A senha lá é opcional.',
                            )}
                        </AlertDescription>
                    </Alert>
                )}

                {laisSemImoview.length === 0 ? (
                    <p className="text-xs text-gray-500 italic">
                        {t('aiAgents.realstate.envioAtendimento.conciliacaoTodosPareados', 'Todo mundo já está pareado.')}
                    </p>
                ) : (
                    <div className="rounded-xl border border-gray-100 overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColLais', 'Usuário LAIS')}</TableHead>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColImovielSel', 'Corresponde a (Imoview)')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {laisSemImoview.map((lu) => {
                                    const selecionado = pareamentos[lu.laisId];
                                    const opcoes = imoviewSemLais.filter(
                                        (u) => !codigosUsados.has(u.codigoUsuario) || (selecionado && selecionado.codigoUsuario === u.codigoUsuario),
                                    );
                                    return (
                                        <TableRow key={lu.laisId}>
                                            <TableCell className="text-sm">
                                                <div>{lu.nome}</div>
                                                {lu.email && <div className="text-xs text-gray-400">{lu.email}</div>}
                                            </TableCell>
                                            <TableCell>
                                                <Select
                                                    value={selecionado ? String(selecionado.codigoUsuario) : ''}
                                                    onValueChange={(value) => escolher(lu.laisId, value)}
                                                >
                                                    <SelectTrigger className={`${INPUT_CLS} max-w-sm`}>
                                                        <SelectValue placeholder={t('aiAgents.realstate.envioAtendimento.conciliacaoSelectPlaceholder', 'Selecione...')} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {opcoes.map((u) => (
                                                            <SelectItem key={u.codigoUsuario} value={String(u.codigoUsuario)}>
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm">{u.nome}</span>
                                                                    <span className="text-xs text-gray-500">{[`#${u.codigoUsuario}`, u.cargo, u.email].filter(Boolean).join(' · ')}</span>
                                                                </div>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
            )}

            {/* Passo 3 */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-[#7C3AED]">
                            {t('aiAgents.realstate.envioAtendimento.passo3Label', 'Passo 3 — Senha dos usuários (opcional)')}
                        </Label>
                        <p className="text-xs text-gray-500 mt-1">
                            {t(
                                'aiAgents.realstate.envioAtendimento.passo3Hint',
                                'Não é necessária para receber atendimentos — quem cria é a conta do Passo 1 e o atendimento é transferido pelo código do Imoview. Preencha só se quiser guardar a credencial pessoal de alguém.',
                            )}
                        </p>
                    </div>
                    {pendentesSenha.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setRevelarSenhas((v) => !v)}
                            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors shrink-0"
                        >
                            <Iconify icon={revelarSenhas ? 'eva:eye-off-outline' : 'eva:eye-outline'} width={14} />
                            {revelarSenhas
                                ? t('aiAgents.realstate.envioAtendimento.conciliacaoOcultarSenhas', 'Ocultar senhas')
                                : t('aiAgents.realstate.envioAtendimento.conciliacaoMostrarSenhas', 'Mostrar senhas')}
                        </button>
                    )}
                </div>

                {pendentesSenha.length === 0 ? (
                    <p className="text-xs text-gray-500 italic">
                        {t('aiAgents.realstate.envioAtendimento.passo3Vazio', 'Ninguém pendente — todo mundo já tem credencial ou ainda não foi pareado no Passo 2.')}
                    </p>
                ) : (
                    <div className="rounded-xl border border-gray-100 overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColLais', 'Usuário LAIS')}</TableHead>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColImoview', 'Usuário Imoview')}</TableHead>
                                    <TableHead>{t('aiAgents.realstate.envioAtendimento.conciliacaoColSenhaObrigatoria', 'Senha Imoview')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pendentesSenha.map((p) => (
                                    <TableRow key={p.laisId}>
                                        <TableCell className="text-sm">{p.nomeLais}</TableCell>
                                        <TableCell className="text-sm text-gray-500">
                                            <div>{p.nome}</div>
                                            <div className="text-xs text-gray-400 font-mono">#{p.codigoUsuario}</div>
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                type={revelarSenhas ? 'text' : 'password'}
                                                placeholder={p.comSenha
                                                    ? t('aiAgents.realstate.envioAtendimento.conciliacaoSenhaConfiguradaPlaceholder', 'Já configurada')
                                                    : t('aiAgents.realstate.envioAtendimento.conciliacaoSenhaOpcionalPlaceholder', 'Opcional')}
                                                value={senhas[p.laisId] || ''}
                                                onChange={(e) => alterarSenha(p.laisId, e.target.value)}
                                                className={`${INPUT_CLS} max-w-[180px]`}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {resultadoSalvar && (
                    <Alert className={resultadoSalvar.ok ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}>
                        <AlertDescription className="text-xs">{resultadoSalvar.msg}</AlertDescription>
                    </Alert>
                )}

                {pendentesSenha.length > 0 && (
                    <div className="flex justify-end">
                        <ButtonLoading
                            loading={salvando}
                            disabled={prontosParaSalvar.length === 0}
                            onClick={handleSalvar}
                            className="h-9 gap-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold rounded-lg shadow-lg shadow-violet-500/20 transition-all"
                        >
                            {t('aiAgents.realstate.envioAtendimento.conciliacaoSalvar', 'Salvar')}
                        </ButtonLoading>
                    </div>
                )}
            </div>
        </div>
    );
}
